import {Page} from '../../../common/constant/page';
import {
  getImageUrl,
  getVideoSources,
  getContentProps,
  I_ContentMeta,
  getPlatformSource,
  getTorosInfo,
  I_TorosLog,
  getColor,
  getAdBanner,
  I_AdBanner,
} from '../../../common/state/apiGet';
import {IAdBanner, IContentMeta, ITorosInfo} from '../../../common/type';
import {
  DEFAULT_DATE_FORMAT,
  formatFromServerDate,
} from '../../../common/util/date';
import {parseLandingUriFromServer} from '../../../common/util/PageRouter';
import {toCamelCase} from '../../../common/util/string';
import {getDelta} from '../../common/common';
import {
  IMainAdditionalProps,
  IBigOverContent,
  IMainContent,
  IMainItem,
  HomeTabPage,
} from '../../common/type';
import {
  IRecommendContentCard,
  ISpecialCard,
  ISpecialContent,
  ISpecialResource,
  ISpecialResourceType,
  IWaitfree,
  IRanking,
  IRankingTabContents,
} from '../type';

interface I_Main {
  sections: I_MainItem[];
  advertisements?: I_AdBanner[];
  tag?: string;
}
interface I_MainItem {
  id: string;
  title?: string;
  module: string;
  placement: string;
  tag: string;
  cardGroups?: Array<{
    cards?: I_MainItemCard[];
    lastModified?: string;
  }>;
}
interface I_MainItemCard {
  id: string;
  key: string;
  operationId?: string;
  content: I_ContentMeta;
  additional: I_MainAdditional;
}
interface I_MainAdditional {
  label: string;
  labelBackgroundColor: string;
  subLabel: string;
  landingType: 'CONTENT' | 'EVENT_PAGE';
  url: string;
  video?: string;
  customBadges?: string[];
  titleText?: string;
}

export function getTodayFreeSeries({sections = []}: I_Main): IMainItem {
  const todayFreeSeries = getMain({sections}, true)[0];

  return {
    ...todayFreeSeries,
    showRankingInfo: false,
    title: undefined,
  };
}

/**
 * 강조형 = 확장카드 + 카드 리스트인 레이아웃
 * GRID_ANCHOR_4, GRID_ANCHOR_7 = 강조형 (자동)
 * OPERATION_4, OPERATION_7 = 강조형 (운영)
 */
const MAIN_LIST_SLICE_COUNT_MAP: {[key in string]: number} = {
  GRID_ANCHOR_7: 8,
  OPERATION_7: 8,
};
export function getMain(
  {sections = []}: I_Main,
  needAllContents?: boolean
): IMainItem[] {
  return sections
    .map(item => {
      const {
        id,
        tag,
        placement,
        module: moduleType,
        title: _title,
        cardGroups,
      } = item;
      const cards = cardGroups?.[0]?.cards || [];
      let title = _title;
      let bigContent: I_MainItemCard | undefined;
      let contents: I_MainItemCard[] | undefined;
      let showRankingInfo = false;
      let showAuthors = true;

      const isOnlyMainCard =
        placement === 'top_hot_main' || moduleType === 'MAIN';
      const isOnlyCardList = placement.match(/^top_[\w]+_normal$/) !== null;
      const isAdminSection = isOnlyMainCard || isOnlyCardList;

      if (isOnlyMainCard) {
        bigContent = cards[0];
        title = undefined;
        showAuthors = false;
      } else if (isOnlyCardList) {
        contents = cards;
        title = undefined;
      } else {
        // bigContent 없고 + 큰화면 8개만 그려질 수 있도록 기본 8개로 슬라이스
        const sliceCount = MAIN_LIST_SLICE_COUNT_MAP[moduleType] || 8;
        showRankingInfo = true;
        [bigContent, ...contents] = cards;
        contents = needAllContents ? contents : contents.slice(0, sliceCount);
      }
      return {
        id,
        tag,
        placement,
        moduleType,
        title,
        showRankingInfo,
        isAdminSection,
        bigContent: bigContent
          ? getMainBigContent(bigContent, {showAuthors, isAdminSection})
          : undefined,
        contents: contents?.map(getMainContent),
      };
    })
    .filter(
      item => !!item.bigContent || (item.contents && item.contents.length > 0)
    );
}
function getMainBigContent(
  {id, content, additional: additionalProps, operationId}: I_MainItemCard,
  {showAuthors, isAdminSection}: {showAuthors: boolean; isAdminSection: boolean}
): IBigOverContent {
  const additional = {...additionalProps};
  const contentProps = getContentProps(
    content,
    [
      'id',
      'sid',
      'title',
      'authors',
      'bgImg',
      'bgColor',
      'bgColorRgb',
      'badges',
      'titleImg',
      'mainImg',
      'mainTitleImg',
      'descLine2',
      'motionVideo',
      'motionStartImg',
      'promotionVideo',
    ],
    {isBigBg: true}
  );
  if (isAdminSection) {
    delete contentProps.promotionVideo;
  } else {
    delete additional.video;
  }
  return {
    ...contentProps,
    ...getMainCommonProps({additional, content: contentProps, operationId}),
    cardId: id,
    showAuthors,
    titleText: additional.titleText,
  };
}

function getMainContent({
  content,
  additional,
  operationId,
}: I_MainItemCard): IMainContent {
  const contentProps = getContentProps(content, [
    'id',
    'sid',
    'title',
    'bgImg',
    'badges',
    'thumbImg',
    'titleImg',
    'bgColor',
    'bgColorRgb',
  ]);
  return {
    ...contentProps,
    ...getMainCommonProps({additional, content: contentProps, operationId}),
  };
}

function getMainCommonProps({
  additional,
  content,
  operationId,
}: {
  additional: I_MainAdditional;
  content: IContentMeta;
  operationId?: string;
}): IMainAdditionalProps {
  const {
    label: text,
    subLabel,
    labelBackgroundColor: bgColor,
    landingType,
    url,
    video,
  } = additional;

  // landingType 이 없으면 랭킹 섹션으로 생각하면 된다. 랭킹 섹션은 무조건 작품 카드다.
  const isContent = landingType === 'CONTENT' || !landingType;

  return {
    label: {text, bgColor},
    landingUrl: isContent
      ? {
          page: Page.Content,
          id: content.id,
          sid: content.sid,
        }
      : parseLandingUriFromServer(url),
    ranking: Number(text),
    delta: getDelta(subLabel),
    isEvent: !isContent,
    landingType,
    operationId,
    eventIpVideo: video ? {sources: getVideoSources(video)} : undefined,
    blackBadgeList: additional?.customBadges,
  };
}

export function getRankingTabContents({
  sections = [],
  tag = '',
}: I_Main): IRankingTabContents {
  let rankingSectionTitle: string | undefined;
  let rankingCards: I_MainItemCard[] = [];
  let lastModified: string | undefined;
  const adminSections: I_MainItem[] = [];

  sections.forEach(section => {
    // 랭킹 섹션 구분은 module = 'RANK' 로 구분한다.
    if (section.module === 'RANK') {
      rankingSectionTitle = section.title;
      rankingCards = section.cardGroups?.[0].cards || [];
      lastModified = section.cardGroups?.[0].lastModified;
    } else {
      adminSections.push(section);
    }
  });

  return {
    adminSectionList: getMain({sections: adminSections}),
    isRankingInProgress: rankingCards.length === 0,
    rankingSectionTitle,
    rankingList: getRankingList(rankingCards, lastModified),
    tag,
  };
}

export function getWufPlus({sections}: I_Main): IMainItem {
  const {
    id,
    tag,
    placement,
    module: moduleType,
    title,
    cardGroups,
  } = sections[0];

  const cards = cardGroups?.[0]?.cards || [];
  const [bigContent, ...contents] = cards;

  return {
    id,
    tag,
    placement,
    moduleType,
    title,
    showRankingInfo: false,
    isAdminSection: false,
    bigContent: bigContent
      ? getMainBigContent(bigContent, {
          showAuthors: true,
          isAdminSection: false,
        })
      : undefined,
    contents: contents?.map(getMainContent),
  };
}

export function getRanking(
  {sections = [], advertisements = []}: I_Main,
  tagData?: I_Main
): IRanking {
  return {
    tabs: sections.map((tab, index) => {
      const {id, title = '', placement} = tab;
      const isRealTimeRanking = tab.placement === 'rank_all';
      return {
        title,
        id,
        placement: `top_${placement}`,
        tag: isRealTimeRanking ? '' : tagData?.sections[index - 1].tag || '',
      };
    }),
    talkBoards: advertisements.map(
      ad => getAdBanner({page: HomeTabPage.Main, data: ad}) as IAdBanner
    ),
  };
}

function getRankingList(rankingCards: I_MainItemCard[], lastModified?: string) {
  return rankingCards.length > 0
    ? {
        bigContent: {
          ...getRankingBigContent(rankingCards[0]),
          lastModified: lastModified
            ? formatFromServerDate(lastModified, `${DEFAULT_DATE_FORMAT} HH:mm`)
            : undefined,
        },
        contents: rankingCards.slice(1).map((card, index) => ({
          ...getRankingContent(card.content, index),
          ...getRankingInfo(card.additional),
        })),
      }
    : undefined;
}

function getRankingBigContent(item: I_MainItemCard): IBigOverContent {
  const content = getRankingContent(item.content, 0);
  return {
    ...content,
    ...getRankingInfo(item.additional),
    cardId: item.id,
    blackBadgeList: item.additional?.customBadges,
    landingUrl: {
      page: Page.Content,
      id: content.id,
      sid: content.sid,
    },
    landingType: 'CONTENT',
    delta: getDelta(item.additional.subLabel),
    showAuthors: true,
  };
}
function getRankingContent(content: I_ContentMeta, index: number) {
  return getContentProps(
    content,
    [
      'id',
      'sid',
      'title',
      'authors',
      'descLine2',
      'mainImg',
      'mainTitleImg',
      'thumbImg',
      'titleImg',
      'bgImg',
      'bgColor',
      'bgColorRgb',
      'badges',
      'motionVideo',
      'motionStartImg',
      'promotionVideo',
    ],
    {isBigBg: index === 0}
  );
}
function getRankingInfo({label, subLabel}: {label: string; subLabel: string}) {
  return {
    ranking: Number(label),
    delta: getDelta(subLabel),
  };
}

export function getRecommendContentCards(data: I_Recommend[] = []): {
  items: IRecommendContentCard[];
  torosInfo?: ITorosInfo;
} {
  const items: IRecommendContentCard[] = [];
  let torosInfo: ITorosInfo | undefined;

  // API 에서 내려주는 순서와 다르다. 운영 -> 운영 마지막 -> 토로스 추천 카드 순으로 재정렬
  const mainFeaturedData = data.find(
    item => item.tag === 'main' && item.placement === 'main_featured'
  );
  const mainFeaturedLastData = data.find(
    item => item.placement === 'main_featured_last'
  );
  const torosData = data.find(item => item.id === 'section_toros');

  const newData = [];
  mainFeaturedData && newData.push(mainFeaturedData);
  mainFeaturedLastData && newData.push(mainFeaturedLastData);
  torosData && newData.push(torosData);

  newData.forEach(apiData => {
    torosInfo = apiData.torosLog ? getTorosInfo(apiData.torosLog) : torosInfo;
    apiData?.cardGroups?.forEach(cardGroup => {
      cardGroup.cards?.forEach((card, index) => {
        const {label, labelBackgroundColor, video, landingType, url} =
          card.additional;
        const content = {
          ...getContentProps(
            card.content,
            [
              'id',
              'sid',
              'title',
              'bgImg',
              'decoImg',
              'badges',
              'mainTitleImg',
              'descLine3',
              'mainImg',
              'motionVideo',
              'motionStartImg',
            ],
            {isBigBg: true}
          ),
        };

        items.push({
          id: card.id,
          sectionId: apiData.id,
          torosOrder: apiData.id === 'section_toros' ? index + 1 : undefined,
          content: {
            ...content,
            label: {
              text: label,
              bgColor: labelBackgroundColor,
            },
            promotionVideo: video
              ? {
                  sources: getVideoSources(video),
                }
              : undefined,
            landingPageInfo:
              landingType === 'CONTENT'
                ? {
                    page: Page.Content,
                    id: content.id,
                    sid: content.sid,
                  }
                : parseLandingUriFromServer(url),
          },
          cardgroupId: card.operationId,
          impression: card.impression,
        });
      });
    });
  });

  return {items, torosInfo};
}

export function getSpecialList(data: I_SpecialList[] = []): ISpecialCard[] {
  const apiData = data[0];
  const result: ISpecialCard[] = [];

  apiData?.cardGroups?.forEach(cardGroup => {
    cardGroup.cards?.forEach(card => {
      const {id, operation} = card;
      const {
        url,
        titleImage,
        labels,
        backgroundImage,
        characterImages,
        decorationImages,
      } = operation;
      result.push({
        id,
        url,
        titleImg: getImageUrl(titleImage, {hasAlpha: true}),
        labels,
        bgImg: getImageUrl(backgroundImage.image),
        mainResources: characterImages.map(src => getSpecialSource(src)),
        decoResources: decorationImages.map(src => getSpecialSource(src)),
        sectionId: apiData.id,
        cardgroupId: card.operationId,
        bridgeId: url.split('/').pop() || '',
      });
    });
  });

  return result;
}

export function getWaitfreeContentList(data: I_WaitfreeList[] = []): IWaitfree {
  const list = data[0]?.cardGroups[0]?.cards || [];
  const waitfreeInfo = data[0]?.gidamoo;
  return {
    waitfreeInfo: {
      hours: waitfreeInfo?.period || 0,
      episodeCount: waitfreeInfo?.availableEpisodeCount || 0,
    },
    contents: list.map(item => {
      const {additional} = item;
      const content = getContentProps(item.content, [
        'id',
        'sid',
        'title',
        'descLine3',
        'mainImg',
        'mainTitleImg',
        'thumbImg',
        'bgImg',
        'badges',
        'authors',
        'motionVideo',
        'motionStartImg',
        'isAdult',
      ]);
      return {
        cardId: item.id,
        label: additional?.label
          ? {
              text: additional.label,
              bgColor: getColor(additional.labelBackgroundColor),
            }
          : undefined,
        landingUrl:
          additional?.landingType === 'CONTENT'
            ? {
                page: Page.Content,
                id: content.id,
                sid: content.sid,
              }
            : additional?.url
              ? parseLandingUriFromServer(additional.url)
              : undefined,
        ...content,
      };
    }),
  };
}

function getSpecialSource(data: I_SpecialResource): ISpecialResource {
  const {videoLoop, resourceType, image, video, videoFirstFrame} = data;
  return {
    isVideoLoop: videoLoop,
    resourceType: toCamelCase(resourceType) as ISpecialResourceType,
    imageUrl: getImageUrl(image, {hasAlpha: resourceType === 'ALPHA_IMAGE'}),
    lottieUrl:
      video && resourceType === 'VECTOR_VIDEO' ? video + '.json' : undefined,
    videoSources:
      video && resourceType !== 'VECTOR_VIDEO'
        ? getVideoSources(video, resourceType === 'ALPHA_VIDEO')
        : [],
    videoPoster: videoFirstFrame
      ? getPlatformSource(videoFirstFrame) +
        (resourceType === 'ALPHA_VIDEO' ? '.png' : '.jpg')
      : undefined,
  };
}

export function getSpecialContent(data: I_SpecialContent): ISpecialContent {
  const {id, contentId, contentSeoId} = data;
  const {firstPage, middlePages, lastPage} = data.pages;
  const {button} = lastPage;

  return {
    id,
    sid: contentSeoId,
    contentId,
    pages: [
      {
        frontResource: getSpecialSource(firstPage.frontResource),
        backgroundImage: getImageUrl(firstPage.backgroundImage),
        titleImage: firstPage.titleImage
          ? getImageUrl(firstPage.titleImage, {hasAlpha: true})
          : undefined,
      },
      ...middlePages.map(page => ({
        frontResource: getSpecialSource(page.frontResource),
        backResource: getSpecialSource(page.backResource),
      })),
      {
        frontResource: getSpecialSource(lastPage.frontResource),
        backResource: getSpecialSource(lastPage.backResource),
        button: button
          ? {
              text: button.text,
              color: button.color,
              url: parseLandingUriFromServer(button.url),
              decoImg: getImageUrl(button.decorationImage, {hasAlpha: true}),
            }
          : undefined,
      },
    ],
  };
}

interface I_Recommend {
  id: string;
  cardGroups: Array<{
    cards: I_RecommendContent[];
  }>;
  placement: string;
  tag?: string;
  torosLog?: I_TorosLog;
}

interface I_RecommendContent {
  id: string;
  content: I_ContentMeta;
  additional: {
    label: string;
    subLabel: string;
    labelBackgroundColor: string;
    video: string;
    landingType: 'CONTENT' | 'EVENT_PAGE' | 'BRIDGE_PAGE';
    url: string;
  };
  key: string;
  operationId?: number;
  impression?: {
    torosImpressionOpt: string;
    torosImpressionId: string;
  };
}

interface I_SpecialList {
  id: string;
  cardGroups: Array<{
    cards: I_SpecialCard[];
  }>;
}

interface I_SpecialCard {
  id: string;
  operation: {
    url: string;
    titleImage: string;
    labels: I_SpecialCardLabel[];
    backgroundImage: I_SpecialCardImage;
    characterImages: I_SpecialResource[];
    decorationImages: I_SpecialResource[];
  };
  operationId?: number;
}

interface I_SpecialCardImage {
  image: string;
}

interface I_SpecialCardLabel {
  text: string;
  emphasized: boolean;
}

interface I_SpecialContent {
  id: number;
  contentSeoId: string;
  contentId: number;
  pages: I_SpecialPages;
}

interface I_SpecialPages {
  firstPage: I_SpecialFirstPage;
  middlePages: I_SpecialMiddlePage[];
  lastPage: I_SpecialLastPage;
}

interface I_SpecialResource {
  videoLoop: boolean;
  image: string;
  video: string;
  videoFirstFrame: string;
  resourceType:
    | 'IMAGE'
    | 'ALPHA_IMAGE'
    | 'VIDEO'
    | 'ALPHA_VIDEO'
    | 'VECTOR_VIDEO';
}

interface I_SpecialFirstPage {
  backgroundImage: string;
  frontResource: I_SpecialResource;
  titleImage?: string;
}

interface I_SpecialMiddlePage {
  frontResource: I_SpecialResource;
  backResource: I_SpecialResource;
}

interface I_SpecialLastPage extends I_SpecialMiddlePage {
  button: {
    decorationImage: string;
    text: string;
    color: string;
    url: string;
  };
}

export interface I_WaitfreeList {
  cardGroups: Array<{
    cards: I_WaitfreeContent[];
  }>;
  gidamoo: {
    period: number;
    availableEpisodeCount: number;
  };
  id: string;
  module: string;
  placement: string;
  tag: string;
  title: string;
}
interface I_WaitfreeContent {
  id: string;
  key: string;
  additional?: {
    label: string;
    labelBackgroundColor: string;
    landingType: string;
    url: string;
  };
  content: I_ContentMeta;
}
